import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { MethodDetails } from '../../../shared/ontology/model/method-details';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JhiEventManager } from '../../../shared/core-utils';
import { AlertService } from '../../../shared/alert/alert.service';
import { OntologyContext } from '../../ontology.context';
import { finalize } from 'rxjs/internal/operators/finalize';
import { MethodService } from '../../../shared/ontology/service/method.service';
import { PopupService } from '../../../shared/modal/popup.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-method-dialog',
    templateUrl: './method-dialog.component.html'
})
export class MethodDialogComponent implements OnInit, OnDestroy {
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly SPECIAL_CHARACTERS = '^[^<>?|]*$';
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;
    protected readonly ModeView = ModeView;
    protected readonly NUM_EDITABLE_FIELDS = 2;

    isLoading: boolean;
    modeView: ModeView;
    methodDetails!: MethodDetails;
    methodId: string | null = null;
    variableId: string | null = null;

    constructor(public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private router: Router,
                private eventManager: JhiEventManager,
                private methodService: MethodService,
                private alertService: AlertService,
                private ontologyContext: OntologyContext,
                private translateService: TranslateService) {
        this.methodDetails = new MethodDetails();
    }

    ngOnInit(): void {
        this.modeView = this.methodId ? ModeView.VIEW : ModeView.CREATE;
        if (this.modeView === ModeView.VIEW) {
            this.isLoading = true;
            this.methodService.getMethodId(this.methodId).pipe(finalize(() => this.isLoading = false)).toPromise().then(methodDetails => {
                this.ontologyContext.methodDetails = methodDetails;
                this.methodDetails = structuredClone(this.ontologyContext.methodDetails);
                this.isLoading = false;
            });
        }
    }

    edit() {
        this.modeView = ModeView.EDIT;
    }

    discard() {
        if (this.modeView === ModeView.CREATE) {
            this.clear();
        } else {
            this.modeView = ModeView.VIEW;
            this.methodDetails = structuredClone(this.ontologyContext.methodDetails);
        }
    }

    isDeletable() {
        return this.methodDetails?.metadata?.deletable;
    }

    ngOnDestroy(): void {
        this.ontologyContext.methodDetails = null;
    }

    clear() {
        this.navigateToVariableView();
    }

    private navigateToVariableView() {
        this.activeModal.dismiss('cancel');
        setTimeout(() => {
            if (this.variableId) {
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', this.variableId, 'view'] } }],
                    { queryParamsHandling: 'merge' }
                );
            } else if (this.ontologyContext.variableDetails) {
                if (this.methodDetails.id) {
                    this.ontologyContext.variableDetails.method = structuredClone(this.methodDetails);
                }
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', 'new'] } }],
                    { queryParamsHandling: 'merge' }
                );
            }
        });
    }

    save() {
        this.isLoading = true;
        if (this.modeView === ModeView.EDIT) {
            this.methodService.update(this.methodDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.methods.modal.method.edited.success');
                this.notifyChanges();
            });
        } else {
            this.methodService.create(this.methodDetails).pipe(
                finalize(() => this.isLoading = false))
                .subscribe((res) => {
                    this.methodDetails.id = res.body?.id;
                    this.alertService.success('ontology-manager.tab.methods.modal.method.created.success');
                    this.notifyChanges();
                });
        }
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'methodPaneViewChanged' });
        this.clear();
    }

    protected showNoEditableFieldAlert() {
        return this.modeView === ModeView.EDIT && this.methodDetails?.metadata?.editableFields.length < this.NUM_EDITABLE_FIELDS;
    }

    protected isFormValid(f) {
        return f.form.valid && !this.isLoading && this.methodDetails.name;
    }

    protected delete() {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        const message = 'ontology-manager.method.confirm.deletion';
        confirmModalRef.componentInstance.message = this.translateService.instant(message);
        confirmModalRef.result.then(() => {
            this.isLoading = true;
            this.methodService.delete(this.methodDetails.id).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.methods.modal.method.deleted.success');
                this.notifyChanges();
            });
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapePressed(event: KeyboardEvent): void {
        event.preventDefault();
        this.navigateToVariableView();

    }
}

@Component({
    selector: 'jhi-method-popup',
    template: ''
})
export class MethodPopupComponent implements OnInit {

    constructor(private popupService: PopupService,
                private route: ActivatedRoute) {
    }

    ngOnInit(): void {
        const id = this.route.snapshot.params['id'];
        const variableId = this.route.snapshot.params['variableId'];
        const modalRef = this.popupService.open(MethodDialogComponent as Component, { modalDialogClass: 'modal-lg', backdrop: 'static' });
        modalRef?.then(ngmodalRef => {
            ngmodalRef.componentInstance.methodId = id ? id : null;
            ngmodalRef.componentInstance.variableId = variableId ? variableId : null;
        });
    }

}
